import { useQuery } from "@tanstack/react-query";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import TableBody from "@mui/material/TableBody";
import Alert from "@mui/material/Alert";
import Chip from "@mui/material/Chip";
import { Bar, BarChart, CartesianGrid, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { PageHeader } from "@/components/common/PageHeader";
import { KpiCard } from "@/components/common/KpiCard";
import { QueryState } from "@/components/common/AsyncState";
import { StatusChip } from "@/components/common/StatusChip";
import { fetchStudentCabinet } from "@/api/dashboard";
import { fetchPublicSettings } from "@/api/settings";
import { ATTENDANCE_STATUS_LABELS, CHARGE_STATE_LABELS, DAYSET_LABELS, daysToDayset } from "@/types/enums";
import { formatDate, formatMoney, formatPercent, periodLabel } from "@/utils/format";

/** Цвет столбца оценки относительно шкалы (аналог `c4`/`c3`/`c1` из прототипа). */
function gradeColor(grade: number, scale: number): string {
  if (grade >= scale * 0.8) return "#2E9E5B";
  if (grade >= scale * 0.6) return "#D8A315";
  return "#D64545";
}

export function MyCabinetPage() {
  const { data, isLoading, error } = useQuery({ queryKey: ["student-cabinet"], queryFn: fetchStudentCabinet });
  const { data: settings } = useQuery({ queryKey: ["public-settings"], queryFn: fetchPublicSettings });
  const gradeScale = settings?.gradeScale ?? 5;

  return (
    <>
      <PageHeader title="Мой кабинет" subtitle="Витрина ученика — курс, посещаемость, оплата, расписание" />
      <QueryState isLoading={isLoading} error={error}>
        {data && (
          <Stack className="crm-cabinet" spacing={3}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <KpiCard label="Посещаемость" value={formatPercent(data.attendancePercent)} hint={`${data.lessons.length} уроков`} />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <KpiCard label="Средний балл" value={data.averageGrade ? String(data.averageGrade) : "—"} hint={`шкала 1–${gradeScale}`} />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <KpiCard
                  label="К оплате"
                  value={data.debt > 0 ? formatMoney(data.debt) : "Нет долга"}
                  hint={data.debt > 0 && data.nextPaymentDueDate ? `до ${formatDate(data.nextPaymentDueDate)}` : data.debt <= 0 ? "всё оплачено" : undefined}
                  color={data.debt > 0 ? "error.main" : "success.main"}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <KpiCard
                  label="Ближайший урок"
                  value={data.nextLesson ? formatDate(data.nextLesson.date) : "—"}
                  hint={data.nextLesson ? `${data.nextLesson.subjectName} · ${data.nextLesson.timeSlot}` : undefined}
                />
              </Grid>
            </Grid>

            <Grid container spacing={2}>
              <Grid item xs={12} md={5}>
                <Paper className="crm-cabinet__schedule" variant="outlined" sx={{ p: 2.5, height: "100%" }}>
                  <Typography variant="subtitle1" fontWeight={600} mb={1.5}>
                    Моё расписание
                  </Typography>
                  <Stack spacing={1.5}>
                    {data.groups.map((g, index) => {
                      const dayset = daysToDayset(g.days);
                      return (
                        <Stack key={index} direction="row" spacing={1.5} alignItems="flex-start">
                          <Chip label={g.timeSlot} size="small" variant="outlined" />
                          <Stack flex={1}>
                            <Typography variant="body2" fontWeight={600}>
                              {g.subjectName}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {(dayset && DAYSET_LABELS[dayset]) ?? g.days.join(", ")} · {g.roomName} · {g.teacherName}
                            </Typography>
                          </Stack>
                          <Typography variant="caption" color="text.secondary">
                            {g.level}
                          </Typography>
                        </Stack>
                      );
                    })}
                    {data.groups.length === 0 && (
                      <Typography variant="body2" color="text.secondary">
                        Вы пока не записаны в группу.
                      </Typography>
                    )}
                  </Stack>
                </Paper>
              </Grid>
              <Grid item xs={12} md={7}>
                <Paper className="crm-cabinet__grades" variant="outlined" sx={{ p: 2.5, height: "100%" }}>
                  <Typography variant="subtitle1" fontWeight={600} mb={1}>
                    Оценки за последние уроки
                  </Typography>
                  {data.recentGrades.length > 0 ? (
                    <ResponsiveContainer width="100%" height={190}>
                      <BarChart data={[...data.recentGrades].reverse().map((g) => ({ ...g, label: formatDate(g.date).slice(0, 5) }))}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} />
                        <XAxis dataKey="label" />
                        <YAxis allowDecimals={false} domain={[0, gradeScale]} />
                        <Tooltip />
                        <Bar dataKey="grade" radius={[4, 4, 0, 0]}>
                          {data.recentGrades.map((g, index) => (
                            <Cell key={index} fill={gradeColor(g.grade, gradeScale)} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      Оценок пока нет.
                    </Typography>
                  )}
                </Paper>
              </Grid>
            </Grid>

            <Paper className="crm-data-panel crm-cabinet__diary" variant="outlined">
              <Typography variant="subtitle1" fontWeight={600} p={2} pb={0}>
                Дневник
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Дата</TableCell>
                    <TableCell>Предмет</TableCell>
                    <TableCell>Тема</TableCell>
                    <TableCell>Был</TableCell>
                    <TableCell align="right">Оценка</TableCell>
                    <TableCell>Домашнее задание</TableCell>
                    <TableCell>Комментарий</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.lessons.map((lesson, index) => (
                    <TableRow key={index}>
                      <TableCell>{formatDate(lesson.date)}</TableCell>
                      <TableCell>{lesson.subjectName}</TableCell>
                      <TableCell>{lesson.topic ?? "—"}</TableCell>
                      <TableCell>
                        <StatusChip label={ATTENDANCE_STATUS_LABELS[lesson.status as keyof typeof ATTENDANCE_STATUS_LABELS] ?? lesson.status} />
                      </TableCell>
                      <TableCell align="right">{lesson.grade ?? "—"}</TableCell>
                      <TableCell>{lesson.homeworkDone === null || lesson.homeworkDone === undefined ? "—" : lesson.homeworkDone ? "Сдано" : "Не сдано"}</TableCell>
                      <TableCell>{lesson.comment ?? "—"}</TableCell>
                    </TableRow>
                  ))}
                  {data.lessons.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        Записей в дневнике пока нет.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </Paper>

            <Paper className="crm-data-panel crm-cabinet__payments" variant="outlined">
              <Stack direction="row" justifyContent="space-between" alignItems="center" p={2} pb={0}>
                <Typography variant="subtitle1" fontWeight={600}>
                  Оплаты
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  оплачено пакетов: {data.paidChargesCount} из {data.totalChargesCount}
                </Typography>
              </Stack>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Период</TableCell>
                    <TableCell>Предмет</TableCell>
                    <TableCell align="right">Сумма</TableCell>
                    <TableCell align="right">Оплачено</TableCell>
                    <TableCell align="right">Остаток</TableCell>
                    <TableCell>Срок</TableCell>
                    <TableCell>Статус</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.charges.map((charge, index) => (
                    <TableRow key={index}>
                      <TableCell>{periodLabel(charge.period)}</TableCell>
                      <TableCell>{charge.subjectName}</TableCell>
                      <TableCell align="right">{formatMoney(charge.amount)}</TableCell>
                      <TableCell align="right">{formatMoney(charge.paid)}</TableCell>
                      <TableCell align="right">{charge.left > 0 ? formatMoney(charge.left) : "0"}</TableCell>
                      <TableCell>{formatDate(charge.dueDate)}</TableCell>
                      <TableCell>
                        <StatusChip label={CHARGE_STATE_LABELS[charge.status]} />
                      </TableCell>
                    </TableRow>
                  ))}
                  {data.charges.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        Начислений нет.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
              {data.debt > 0 && (
                <Alert severity="info" sx={{ m: 2, mt: 1.5 }}>
                  Оплатить можно наличными на ресепшене, картой, через Click или Payme. Реквизиты уточните у администратора: {data.branchPhone}
                </Alert>
              )}
            </Paper>

            <Paper className="crm-cabinet__messages" variant="outlined" sx={{ p: 2.5 }}>
              <Typography variant="subtitle1" fontWeight={600} mb={1.5}>
                Сообщения от центра
              </Typography>
              <Stack spacing={1.5}>
                {data.notifications.map((n, index) => (
                  <Stack key={index} sx={{ borderLeft: "2px solid", borderColor: "divider", pl: 1.5 }}>
                    <Typography variant="caption" color="text.secondary">
                      {formatDate(n.sentAt)} · {n.channel === "SMS" ? "SMS" : "Telegram"}
                    </Typography>
                    <Typography variant="body2">{n.text}</Typography>
                  </Stack>
                ))}
                {data.notifications.length === 0 && (
                  <Typography variant="body2" color="text.secondary">
                    Сообщений пока нет.
                  </Typography>
                )}
              </Stack>
            </Paper>
          </Stack>
        )}
      </QueryState>
    </>
  );
}
